import React, { useMemo } from 'react';

import SummaryItem from 'components/Payments/NewPayment/PaymentContent/components/SummaryItem';
import Allowed from 'components/shared/Permissions/Allowed';

import { calculateTotals } from './lib/helpers';

const ReceiptSummary = ({ transaction, showDetails, transactions, isPatient }) => {
  const {
    id,
    amount_paid_self,
    surcharge_amount,
    type,
    invoice_id,
    parent_id,
    appointment_id,
    amount_refunded
  } = transaction || {};

  const isParent = !parent_id && !invoice_id && !appointment_id;

  const isWriteoff = type === 'writeoff';

  const amountPaid = amount_paid_self - (surcharge_amount || 0) || 0;

  const amountPaidWithSurcharge = amountPaid + (surcharge_amount || 0);

  const totals = useMemo(() => calculateTotals(transactions), [transactions]);

  const previousPayments = totals.totalAmountPaid - (!isWriteoff ? amountPaid : 0);

  const hasInvoiceOrManyTransactions =
    !!transaction?.invoice || (transactions.length > 0 && transaction?.id !== transactions[0]?.id);

  const amountDue = transactions?.reduce((acc, t) => {
    if (!t.invoice) return acc;

    const { total_amount = 0, discount = 0, adjustment = 0, tax = 0 } = t.invoice || {};

    return (
      acc +
      total_amount -
      discount -
      adjustment +
      tax -
      (!isParent ? previousPayments : 0) +
      (isWriteoff ? amountPaid : 0)
    );
  }, 0);

  return (
    showDetails && (
      <Allowed requiredPermissions={isPatient ? '' : 'payments.read'}>
        <div className="flex w-full max-w-[332px] flex-col items-end gap-4 self-end sm:max-w-full">
          <div className="flex w-full flex-col border-0 border-b border-solid border-b-neutral-900 py-1">
            <SummaryItem
              className="h-9"
              label="Subtotal"
              value={
                !hasInvoiceOrManyTransactions
                  ? amountPaid + (amount_refunded || 0)
                  : totals.totalAmount
              }
            />
            <SummaryItem className="h-9" label="Technology fee" value={surcharge_amount} />
            {amount_refunded > 0 && (
              <SummaryItem className="h-9" label="Refund" value={amount_refunded * -1} />
            )}
            {hasInvoiceOrManyTransactions && (
              <>
                <SummaryItem className="h-9" label="Discount" value={totals.totalDiscount * -1} />

                {previousPayments > 0 && (
                  <SummaryItem
                    className="h-9"
                    label="Previous Payments"
                    value={previousPayments * -1}
                  />
                )}
                {totals.totalAdjustment > 0 && !isWriteoff && (
                  <SummaryItem
                    className="h-9"
                    label="Adjustment"
                    value={totals.totalAdjustment * -1}
                  />
                )}
                <SummaryItem className="h-9" label="Tax" value={totals.totalTax} />
              </>
            )}
            <SummaryItem
              className="h-9"
              label="Current Amount Due"
              value={
                hasInvoiceOrManyTransactions
                  ? amountDue + (surcharge_amount || 0)
                  : totals.totalBalance + amountPaid + (surcharge_amount || 0)
              }
            />
            {id && (
              <SummaryItem
                enabled
                className="h-10"
                weight="semibold"
                priceWeight="semibold"
                label="Total Amount Paid"
                value={amountPaidWithSurcharge}
              />
            )}
            <div className="border-0 border-b border-solid border-b-neutral-900 py-1" />
          </div>
          {id && (
            <div className="flex w-full flex-col rounded-md bg-neutral-50 px-2">
              {totals?.totalBalance > 0 && (
                <SummaryItem
                  className="h-9"
                  label="Remaining Balance"
                  value={totals?.totalBalance}
                />
              )}
              <SummaryItem
                className="h-9"
                label="Unallocated amount"
                value={(amountDue - amountPaid) * -1}
              />
            </div>
          )}
        </div>
      </Allowed>
    )
  );
};

export default ReceiptSummary;
